/**
 * The client's half of the symbology contract (doc/in-progress/
 * site-tagging-and-symbology.md §4.6): turn the district's catalog — resolved
 * server side once per session (§4.5) — into the MapLibre paint expressions the
 * Site layers draw with, and into the swatch CSS the pickers and the ledger show.
 *
 * Nothing here resolves precedence. §4.2's order is answered by the catalog the
 * server sends: arms 1 and 2 arrive per tag (`catalog.tags[].style`), arms 3→5
 * arrive pre-merged per kind (`catalog.kinds[].default`). The only job left is to
 * spell that as one `match` on the tile's `symbol_tag_id` (§4.4).
 */

import type { ExpressionSpecification } from "maplibre-gl";

import { SITE_FILL_COLOR } from "@/lib/mapReviewLayers";
import type { SymbolStyle, SymbologyCatalog, SymbolTagStyle } from "@/type/api";

/** The paint attributes a symbol states, in the order the map reads them. Both
 * `Symbol` and `SymbolStyle` carry every one; null means "this arm does not
 * paint it" and falls to the product default (§4.3). */
export type StyleAttr =
	| "fill_color"
	| "stroke_color"
	| "fill_opacity"
	| "stroke_opacity"
	| "stroke_width"
	| "size";

/** The attribute set a preview swatch reads. `SymbolStyle` and `Symbol` both
 * carry it and nothing else is needed to draw one, so the pickers can swatch a
 * library row and a catalog row through the same function. */
export type StyleFields = Pick<SymbolStyle, StyleAttr | "icon_uuid">;

/** The kind the Site map draws. The catalog carries one arm set per kind and
 * `site` is the only taggable one in v1 (§9.4), so it is the only kind a paint
 * expression is built for. */
const SITE_KIND = "site";

/** A point marker's size when neither the chosen style nor the product default
 * states one: `default_size` is NULL for the `site` kind (§1 step 2), since an
 * area kind has no size to state and the map's own marker is what draws it. */
const MARKER_SIZE = 6;

/** The value each attribute falls to when neither the chosen style nor the kind's
 * product default states one.
 *
 * These are the numbers `symbology_subject_kind` seeds for `site` — the values
 * ts/view/MapReview.vue paints a Site with today — repeated here rather than
 * read, because this is the expression's last resort when a catalog is stale or
 * omits the kind, and an expression cannot await a reload. For a colour the
 * neutral is not a symbology choice at all: §4.3 makes a null fill mean "the
 * map's own base rendering", which for a Site is the assignment colour that is
 * already the layer's default, so falling back to the fabric neutral keeps an
 * unsymbolized Site looking unassigned rather than inventing a hue. A `match`
 * cannot put the assignment expression in a branch — outputs must be literals —
 * so the caller composes that channel at the layer, not here. */
const BASE_RENDER: Record<StyleAttr, string | number> = {
	fill_color: SITE_FILL_COLOR,
	stroke_color: SITE_FILL_COLOR,
	fill_opacity: 0.06,
	stroke_opacity: 0.6,
	stroke_width: 0.5,
	size: MARKER_SIZE,
};

/**
 * One style value as a `match` output, i.e. as a literal MapLibre accepts.
 *
 * A stated value passes through: colours, opacities, widths and sizes are already
 * literals of the right type. A null value means the winning arm does not paint
 * that attribute (§4.3), so it falls to the kind's product default — `builtin`,
 * §4.2 arm 5, which is exactly the row
 * `chosen.attr ?? productDefault(K).attr` names — and when the product default is
 * null too (it is, for the `site` kind's fill, stroke and size) to BASE_RENDER.
 * Every branch therefore carries a literal: a null output would make the whole
 * paint expression invalid, and a `match` output cannot itself be an expression.
 * The attribute is a parameter rather than something the caller's value implies
 * because it is what decides which product default the fallback reads — two
 * attributes can both be null and fall to different values.
 */
export function exprFor(
	catalog: SymbologyCatalog,
	attr: StyleAttr,
	value: string | number | null | undefined,
): string | number {
	if (value != null) return value;
	// A catalog that omits the kind (a stale or hand-built payload) has no
	// product default to read, so the lookup below leaves BASE_RENDER to answer
	// rather than throwing in the middle of a map paint.
	const productDefault = catalog.kinds.find((kind) => kind.code === SITE_KIND)
		?.builtin[attr];
	if (productDefault != null) return productDefault;
	return BASE_RENDER[attr];
}

/**
 * The Site layer's paint for one attribute, spelled as §4.6 states it: a `match`
 * on the tile's `symbol_tag_id` with one branch per catalog tag and the kind's
 * pre-merged default (§4.2 arms 3→5) as the default branch.
 *
 * An untagged Site's feature carries no `symbol_tag_id` at all — MVT omits a NULL
 * column — which falls to the default branch without a second predicate, so the
 * data-driven expression needs no "is it tagged" test.
 *
 * The catalog's `tags` already carry every retired tag a live assignment still
 * names (§7.3), so a Site tagged with a since-retired tag keeps its style; and a
 * district with no tags yet still yields a valid expression, because MapLibre's
 * `match` demands at least one label — the impossible one (-1 is never a tag id)
 * is the same escape siteAssignmentColorExpr takes.
 */
export function symbolPaint(
	catalog: SymbologyCatalog,
	attr: StyleAttr,
	base?: ExpressionSpecification,
): ExpressionSpecification {
	// A null value on a tag means that arm does not paint the attribute, so the
	// feature keeps the map's own base rendering (§4.3). A `match` output cannot
	// itself be an expression, so when the caller states that base rendering the
	// expression splits in two: a boolean `match` over the tags that DO state the
	// attribute selects the colour match, and everything else — untagged, or
	// tagged by an arm that states nothing — is the base.
	const tags = catalog.tags;
	if (base != null) {
		const stated = tags.filter((tag) => tag.style[attr] != null);
		if (stated.length === 0) return base;
		const selectsStated: (number | boolean | string)[] = [];
		const values: (number | string)[] = [];
		for (const tag of stated) {
			selectsStated.push(tag.tag_id, true);
			values.push(tag.tag_id, exprFor(catalog, attr, tag.style[attr]));
		}
		const fallback = exprFor(
			catalog,
			attr,
			catalog.kinds.find((kind) => kind.code === SITE_KIND)?.default[attr],
		);
		return [
			"case",
			["match", ["get", "symbol_tag_id"], ...selectsStated, false],
			["match", ["get", "symbol_tag_id"], ...values, fallback],
			base,
		] as unknown as ExpressionSpecification;
	}
	const branches: (number | string)[] = [];
	for (const tag of tags) {
		branches.push(tag.tag_id, exprFor(catalog, attr, tag.style[attr]));
	}
	const fallback = exprFor(
		catalog,
		attr,
		catalog.kinds.find((kind) => kind.code === SITE_KIND)?.default[attr],
	);
	// The label/output pairs are spread from a list whose length the type system
	// cannot know, and MapLibre's spec wants a fixed tuple — hence the double
	// assertion, the same escape ts/lib/mapReviewLayers.ts uses for its own
	// hand-built paint expressions.
	return [
		"match",
		["get", "symbol_tag_id"],
		...(branches.length > 0 ? branches : [-1, fallback]),
		fallback,
	] as unknown as ExpressionSpecification;
}

/**
 * The url an uploaded icon is served from. The server's blob endpoints are
 * same-origin, path-only references the SPA puts straight in `src` — the shape
 * `platform/types/image.go` states for `url_content` (`/api/image/{uuid}/content`)
 * — and a symbol's icon is `/api/symbol/icon/{uuid}` (§7.2). Null when the style
 * names no icon, so a caller can `v-if` on the answer rather than build a url to
 * nothing.
 */
export function symbolIconURL(uuid: string | null | undefined): string | null {
	return uuid ? `/api/symbol/icon/${uuid}` : null;
}

/**
 * A style as CSS for a preview swatch: the fill as the background, the stroke as
 * the border, each keeping its own opacity, because those are the two channels
 * the map paints separately.
 *
 * Opacity is folded into the colour rather than set as the element's `opacity`,
 * which would fade the stroke along with the fill — something the map never does
 * (the Site outline is 0.6 over a 0.06 wash). A null fill or stroke shows the
 * fabric neutral, the same base rendering §4.3 leaves an unsymbolized Site with.
 */
export function styleCss(style: StyleFields): Record<string, string> {
	return {
		backgroundColor: fade(
			style.fill_color ?? SITE_FILL_COLOR,
			style.fill_opacity,
		),
		borderColor: fade(
			style.stroke_color ?? SITE_FILL_COLOR,
			style.stroke_opacity,
		),
		borderStyle: "solid",
		borderWidth: `${style.stroke_width}px`,
	};
}

/** The catalog's entry for one tag id, or undefined when this catalog does not
 * carry it (a stale ledger row, or a tag retired and no longer assigned). The
 * ledger's chips and the symbology pane both look a tag up, so the lookup is
 * stated once instead of in each of them. */
export function catalogTag(
	catalog: SymbologyCatalog,
	tagID: number,
): SymbolTagStyle | undefined {
	return catalog.tags.find((tag) => tag.tag_id === tagID);
}

/**
 * A colour with an opacity folded in, as `rgba(r, g, b, a)`. The server states
 * colours as `#rrggbb` — the kind and tag columns are checked against that shape
 * in the schema — so hex is what this parses; anything else is returned untouched
 * rather than guessed at, which keeps an unexpected value visible instead of
 * silently repainting it.
 */
function fade(color: string, opacity: number): string {
	const hex = /^#([0-9a-f]{6})$/i.exec(color);
	if (!hex) return color;
	const packed = parseInt(hex[1], 16);
	return `rgba(${(packed >> 16) & 0xff}, ${(packed >> 8) & 0xff}, ${packed & 0xff}, ${opacity})`;
}
